// TES-442 — the LabPortalen quarantine screen ("Provsvar utan koppling"):
// labels, the rows the status panel shows, and the rules the two actions
// follow. Pure on purpose (no React, only contracts helpers): the unit tests in
// `apps/admin/test` run it with plain `node --test`.
//
// The Swedish strings are PROPOSED copy, awaiting Emelie (TES-442).
import {
  isAdminRole,
  type ClinicianLabOrder,
  type LabPortalenQuarantineItem,
  type LabPortalenQuarantineReason,
  type LabPortalenSyncNowResult,
} from "@care-by-ella/contracts";

/** Why a report could not be attached automatically. Proposed copy. */
export const QUARANTINE_REASON_LABEL: Record<
  LabPortalenQuarantineReason,
  string
> = {
  no_patient: "Ingen patient med personnumret",
  no_open_order: "Ingen öppen provbeställning",
  ambiguous_multiple_orders: "Flera möjliga provbeställningar",
  reservnummer_or_invalid_pnr: "Reservnummer eller ogiltigt personnummer",
  unknown_requisition: "Remissen hör till en avbruten beställning",
  no_known_analytes: "Inga analyser som Ella känner igen",
  unparseable: "Svaret kunde inte läsas",
  patient_mismatch: "Personnumret stämmer inte med beställningen",
  order_feedback_sent: "Återkoppling redan skickad till patienten",
  // TES-1341 — proposed copy.
  not_a_report: "Meddelandet är inget provsvar",
  // TES-1377 — proposed copy.
  multiple_reports: "Filen innehöll flera svar – öppna den i LabPortalen",
  // TES-1616 — proposed copy.
  not_in_panel: "Ingår inte i panelen",
  // TES-1617 — attached, and this row holds the values that could not be
  // stored on the order. The same copy as UNKNOWN_ANALYTE_REASON_LABEL when
  // every value shares the reason; each value shows its own beside it.
  unknown_code: "Okänd analyskod",
  unit_mismatch: "Annan enhet än väntat",
  unconvertible_text: "Textsvar kan inte räknas om",
  // TES-1617 — values with different reasons. Proposed copy, raised as an open question.
  values_not_stored: "Värden som inte kunde sparas på beställningen",
};

// TES-1617 — the order card's clause for a row holding the values an
// attached report could not store. Proposed copy, raised as an open question.
const NOT_STORED_ORDER_TEXT =
  "några värden kunde inte sparas på beställningen och visas bara här";

/**
 * TES-1179 — the same reasons, as the clause after "Orsak:" on a lab order
 * whose answer arrived but was quarantined. Plain Swedish for the doctor
 * reading the order, not the integration's vocabulary. Proposed copy.
 */
export const ORDER_QUARANTINE_REASON_TEXT: Record<
  LabPortalenQuarantineReason,
  string
> = {
  no_known_analytes: "inga analyser kunde tolkas",
  no_open_order: "ingen öppen beställning i tidsfönstret",
  no_patient: "ingen patient med svarets personnummer",
  ambiguous_multiple_orders: "flera beställningar kunde vara svaret",
  patient_mismatch: "personnumret i svaret stämmer inte med patienten",
  reservnummer_or_invalid_pnr: "svaret saknar giltigt personnummer",
  unknown_requisition: "remissen hör till en avbruten beställning",
  order_feedback_sent: "återkoppling var redan skickad till patienten",
  unparseable: "svaret kunde inte läsas",
  // TES-1341 — proposed copy.
  not_a_report: "meddelandet var inget provsvar",
  // TES-1377 — proposed copy.
  multiple_reports: "filen innehöll flera svar och lästes inte",
  // TES-1616 — proposed copy.
  not_in_panel: "några värden ingår inte i panelen och visas bara här",
  // TES-1617 — proposed copy, raised as an open question; one clause for all four.
  unknown_code: NOT_STORED_ORDER_TEXT,
  unit_mismatch: NOT_STORED_ORDER_TEXT,
  unconvertible_text: NOT_STORED_ORDER_TEXT,
  values_not_stored: NOT_STORED_ORDER_TEXT,
};

/** The order-card clause for a reason, or the code for an unknown one. */
export function orderQuarantineReasonText(reason: string): string {
  return (
    (ORDER_QUARANTINE_REASON_TEXT as Record<string, string>)[reason] ?? reason
  );
}

/** The query parameter that opens one report on the quarantine screen. */
export const QUARANTINE_REPORT_PARAM = "rapport";

/** Link from a lab order to its quarantined report on "/lab-quarantine". */
export function quarantineReportHref(quarantineId: string): string {
  return `/lab-quarantine?${QUARANTINE_REPORT_PARAM}=${encodeURIComponent(quarantineId)}`;
}

/** The label for a reason code, or the code itself for one this build does
 *  not know yet (a newer API). */
export function quarantineReasonLabel(reason: string): string {
  return (QUARANTINE_REASON_LABEL as Record<string, string>)[reason] ?? reason;
}

/** Why an analysis was not mapped (the last part of the unknown-analyte
 *  key). Proposed copy. */
export const UNKNOWN_ANALYTE_REASON_LABEL: Record<string, string> = {
  unknown_code: "Okänd analyskod",
  unit_mismatch: "Annan enhet än väntat",
  // TES-1263 — a converting mapping met a text answer ("<10").
  unconvertible_text: "Textsvar kan inte räknas om",
  // TES-1616 — a known code that is not a panel analyte (eGFR, total
  // cholesterol, the LDL/HDL ratio); its values are in the quarantine.
  not_in_panel: "Ingår inte i panelen",
};

export interface UnknownAnalyteRow {
  testMethodCode: string;
  anaName: string;
  unit: string;
  reason: string;
  count: number;
}

/**
 * The status endpoint's `unknownAnalytes` ("TestMethodCode|AnaName|Unit|reason"
 * → count) as table rows, most frequent first, so the mapping work (O7)
 * starts with what the labs send most. A key with fewer parts than expected
 * keeps what it has; nothing is dropped.
 */
export function unknownAnalyteRows(
  unknown: Record<string, number>,
): UnknownAnalyteRow[] {
  return Object.entries(unknown)
    .map(([key, count]) => {
      const [testMethodCode = "", anaName = "", unit = "", reason = ""] =
        key.split("|");
      return { testMethodCode, anaName, unit, reason, count };
    })
    .sort(
      (a, b) =>
        b.count - a.count ||
        a.testMethodCode.localeCompare(b.testMethodCode, "sv"),
    );
}

/** Open reports per reason, most first, labelled. */
export function reasonCountRows(
  byReason: Record<string, number>,
): { reason: string; label: string; count: number }[] {
  return Object.entries(byReason)
    .filter(([, count]) => count > 0)
    .map(([reason, count]) => ({
      reason,
      label: quarantineReasonLabel(reason),
      count,
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "sv"));
}

/**
 * The labs that answered in a report, by their code, each once, in order of
 * appearance. TES-1341: the lab's code (Analysis>Lab / SendUnit AddressCode),
 * never Sample@LaboratoryID, which is the lab's number for one sample.
 */
export function laboratoriesOf(item: LabPortalenQuarantineItem): string[] {
  const seen: string[] = [];
  for (const a of item.analyses) {
    const lab = a.labCode?.trim();
    if (lab && !seen.includes(lab)) seen.push(lab);
  }
  return seen;
}

// TES-1341 — InfoSolutions' two requisition numbers, named for what they
// are. Proposed copy.
/** Requisition@RequisitionID: the performing lab's number. */
export const LAB_LID_LABEL = "Labbets remissnummer (LID)";
/** Requisition@ExternalRequisitionID: the ordering system's number. */
export const LAB_RID_LABEL = "Remiss-id (RID)";
/** A report that named the patient only by a reservnummer. */
export const RESERVE_PATIENT_ID_TEXT =
  "Reservnummer (matchas inte automatiskt)";

/**
 * A mapping's lab scope as staff read it: the lab's code, "Alla" for any
 * lab, or a legacy sample-number scope (TES-1263 rows from before TES-1341),
 * which still matches only that one sample. Proposed copy.
 */
export function mappingScopeText(m: {
  labCode?: string | null;
  laboratoryId: string | null;
}): string {
  if (m.labCode) return m.labCode;
  if (m.laboratoryId) return `Provnummer ${m.laboratoryId} (äldre avgränsning)`;
  return "Alla";
}

/** One analysis's result as staff read it: the number, else the lab's text. */
export function analysisValueText(
  a: LabPortalenQuarantineItem["analyses"][number],
): string {
  if (a.value !== null) return String(a.value).replace(".", ",");
  return a.valueText ?? "–";
}

/**
 * The orders a report can be attached to: the API refuses a cancelled one,
 * so it is not offered. Newest first, the likeliest answer to a fresh report.
 */
export function attachableOrders<
  T extends Pick<ClinicianLabOrder, "id" | "status" | "orderedAt">,
>(orders: readonly T[]): T[] {
  return orders
    .filter((o) => o.status !== "cancelled")
    .sort((a, b) => b.orderedAt.localeCompare(a.orderedAt));
}

// Mirrors attach/dismissLabPortalenQuarantineBodySchema in contracts.
export const RESOLUTION_REASON_MIN = 3;
export const RESOLUTION_REASON_MAX = 500;

/** Null when the reason will pass the API, else the line to show. */
export function resolutionReasonError(reason: string): string | null {
  const len = reason.trim().length;
  if (len < RESOLUTION_REASON_MIN) {
    return `Skriv en orsak (minst ${RESOLUTION_REASON_MIN} tecken).`;
  }
  if (len > RESOLUTION_REASON_MAX) {
    return `Orsaken får vara högst ${RESOLUTION_REASON_MAX} tecken.`;
  }
  return null;
}

/**
 * Only a doctor attaches or dismisses: which order a lab answer belongs to is
 * a clinical call, and admin is read-only by policy (TES-1040). The API is the
 * real gate (`@Roles("doctor")` on both writes); this only keeps the buttons
 * away from someone who would get a 403.
 */
export function canResolveQuarantine(
  role: string | undefined,
  readOnly: boolean,
): boolean {
  return role === "doctor" && !readOnly;
}

/**
 * TES-1630 — "Koppla om provsvaren": doctor and admin (Simon 2026-10-27, "I
 * want admins to have the button"). The re-run applies the sweep's automatic
 * matching by personnummer; it never picks a patient or an order by hand, and
 * the doctor still reviews before the patient sees anything. So it is the
 * same group as "Hämta nu" (a coach holding the TES-1089 admin grant counts
 * as admin), never a coach, never a read-only support view. Attach and
 * dismiss stay on canResolveQuarantine. The API is the real gate.
 */
export function canRerunQuarantine(
  role: string | undefined,
  roles: readonly string[] | undefined,
  readOnly: boolean,
): boolean {
  return canFetchLabPortalenNow(role, roles, readOnly);
}

// ---------------------------------------------------------------------------
// TES-1265 — "Hämta nu"
// ---------------------------------------------------------------------------

/**
 * Doctor and admin may start a sweep now: a named exception to TES-1040
 * (Simon 2026-10-12), because the sweep only fetches what the lab already
 * sent and decides nothing clinical. Its own flag, not canResolveQuarantine:
 * admin is otherwise read-only on this page. A coach holding the TES-1089
 * admin grant counts as admin, as the API's RolesGuard does. Never in a
 * read-only support view. The API is the real gate.
 */
export function canFetchLabPortalenNow(
  role: string | undefined,
  roles: readonly string[] | undefined,
  readOnly: boolean,
): boolean {
  if (readOnly) return false;
  return (
    role === "doctor" ||
    isAdminRole(role) ||
    Boolean(roles?.some((r) => isAdminRole(r)))
  );
}

/** The button's label: "Hämtar…" while a sweep runs. */
export function fetchNowLabel(busy: boolean): string {
  return busy ? "Hämtar…" : "Hämta nu";
}

/**
 * The line shown after a press. `tone` picks the notice style: a sweep that
 * stopped on an error reads as an error even though it ran.
 */
export function syncNowResultText(r: LabPortalenSyncNowResult): {
  text: string;
  tone: "success" | "info" | "error";
} {
  if (!r.ran) {
    switch (r.reason) {
      case "already_running":
        return { text: "En hämtning pågår redan.", tone: "info" };
      case "disabled":
        return { text: "Hämtningen är avstängd.", tone: "info" };
      default:
        return {
          text: "Hämtningen är inte konfigurerad för den här miljön.",
          tone: "info",
        };
    }
  }
  const counts = `Hämtat: ${r.fetched} provsvar (${r.attached} kopplade, ${r.quarantined} i karantän).`;
  if (r.error) {
    return {
      text: `${counts} Hämtningen avbröts: ${r.error}`,
      tone: "error",
    };
  }
  return { text: counts, tone: "success" };
}

// ---------------------------------------------------------------------------
// TES-1263 — analyte mappings ("Koppla till analys")
// ---------------------------------------------------------------------------

/**
 * Only a doctor creates and deactivates mappings: admin reads the list but is
 * read-only by policy (TES-1040); a coach sees none of it. The re-run is NOT
 * covered here: it ties results to a patient, so it follows
 * canResolveQuarantine. The API is the real gate (`@Roles("doctor")` on both
 * writes); read-only support mode hides the actions too.
 */
export function canManageMappings(
  role: string | undefined,
  readOnly: boolean,
): boolean {
  return role === "doctor" && !readOnly;
}

/** The unit comparison the API makes: spelling only (case, spaces, micro
 *  sign), never meaning. Mirrors normalizeUnit in the API's analyte-map. */
export function normalizeUnit(unit: string | null | undefined): string {
  return (unit ?? "").toLowerCase().replace(/\s+/g, "").replace(/[µμ]/g, "u");
}

/** True when the lab's unit is not the panel analyte's, so a factor is
 *  required; false when they are the same, so a factor is refused. */
export function unitNeedsFactor(reportedUnit: string, panelUnit: string) {
  return normalizeUnit(reportedUnit) !== normalizeUnit(panelUnit);
}

/**
 * The factor field as typed: blank is "no factor" (null), a positive number
 * with a decimal comma or point is the factor, anything else is "invalid".
 */
export function parseFactor(input: string): number | null | "invalid" {
  const t = input.trim();
  if (t === "") return null;
  if (!/^\d+(?:[.,]\d+)?$/.test(t)) return "invalid";
  const n = Number(t.replace(",", "."));
  return n > 0 && n <= 1_000_000 ? n : "invalid";
}

/** A value read through the factor, as the API stores it (3 decimals), for
 *  the form's worked example. */
export function convertPreview(value: number, factor: number): number {
  return Math.round(value * factor * 1000) / 1000;
}

/** An unknown-analyte row's parts as the form's starting values ("-" in the
 *  counted key means absent). */
export function mappingDraftFrom(row: UnknownAnalyteRow): {
  testMethodCode: string;
  anaName: string;
  reportedUnit: string;
} {
  const part = (s: string) => (s === "-" ? "" : s);
  return {
    testMethodCode: part(row.testMethodCode),
    anaName: part(row.anaName),
    reportedUnit: part(row.unit),
  };
}

/** The counted key of an unknown-analyte row, as the status endpoint
 *  returns it in `unknownAnalytesNowMapped`. */
export function unknownAnalyteKeyOf(row: UnknownAnalyteRow): string {
  return [row.testMethodCode, row.anaName, row.unit, row.reason].join("|");
}

/** TES-1263 / TES-1620 — the reasons the re-run takes through the
 *  attribution ladder again: unreadable analyses a mapping may now read, and
 *  a known patient with no open order (the sweep now makes the order) or a
 *  personnummer with no patient (she may have registered since). The api's
 *  RERUNNABLE_REASONS is the same set. TES-1631: plus the rows holding the
 *  values an attached posting could not store (the api's
 *  NOT_STORED_REASONS), whose readable values go onto that posting's order. */
const RERUNNABLE_REASONS: ReadonlySet<LabPortalenQuarantineReason> = new Set([
  "no_known_analytes",
  "no_open_order",
  "no_patient",
  "not_in_panel",
  "unknown_code",
  "unit_mismatch",
  "unconvertible_text",
  "values_not_stored",
]);

/** Open reports the re-run can act on: one of those reasons, with an
 *  analysis an active mapping reads. */
export function rerunnableCount(
  items: readonly Pick<
    LabPortalenQuarantineItem,
    "reason" | "mappableAnalyteCount"
  >[],
): number {
  return items.filter(
    (i) =>
      RERUNNABLE_REASONS.has(i.reason) && (i.mappableAnalyteCount ?? 0) > 0,
  ).length;
}
